import { useCallback, useEffect, useRef, useState } from 'react';
import CyberLunaWindow from './CyberLunaWindow';
import styles from './XPVideoPlayer.module.css';

/** mm:ss, with a placeholder until metadata gives us a real duration. */
const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return '0:00';
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const Icon = ({ children }) => (
  <svg viewBox="0 0 12 12" className={styles.icon} aria-hidden="true" focusable="false">
    {children}
  </svg>
);

const PlayIcon = () => <Icon><path d="M3 2l7 4-7 4z" fill="currentColor" /></Icon>;
const PauseIcon = () => <Icon><path d="M3 2h2v8H3zm4 0h2v8H7z" fill="currentColor" /></Icon>;
const StopIcon = () => <Icon><path d="M3 3h6v6H3z" fill="currentColor" /></Icon>;
const FullscreenIcon = () => (
  <Icon>
    <path
      d="M2 4V2h3M10 4V2H7M2 8v2h3M10 8v2H7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </Icon>
);
const SoundIcon = () => (
  <Icon>
    <path d="M2 4.5h2L6.5 2v8L4 7.5H2z" fill="currentColor" />
    <path d="M8.5 4a3 3 0 010 4" fill="none" stroke="currentColor" strokeWidth="1.2" />
  </Icon>
);
const MutedIcon = () => (
  <Icon>
    <path d="M2 4.5h2L6.5 2v8L4 7.5H2z" fill="currentColor" />
    <path d="M8.5 4.5l3 3m0-3l-3 3" fill="none" stroke="currentColor" strokeWidth="1.2" />
  </Icon>
);

/**
 * XPVideoPlayer — an mp4 inside CyberLunaWindow chrome, Windows Media Player style.
 *
 * Two modes:
 *   • 'player'  (default) — poster + XP transport controls; nothing downloads
 *     until play. Use for recaps and live sets where sound matters.
 *   • 'ambient' — autoplays muted on loop, no controls; behaves like a GIF.
 *     Use for short vibe clips and animated flyers.
 *
 * The transport bar is built from real controls rather than the browser's
 * native `controls` chrome, which renders as a modern rounded overlay and
 * looks out of place inside the Luna window frame.
 *
 * @component
 * @param {Object} props
 * @param {string} props.src - Video URL (include process.env.PUBLIC_URL for /public files)
 * @param {string} [props.title] - Window title; defaults to the file name from src
 * @param {string} [props.poster] - Thumbnail shown before play ('player' mode)
 * @param {('player'|'ambient')} [props.mode='player'] - Playback behaviour
 * @param {string} [props.label] - Accessible name for the video itself
 * @param {string} [props.className] - Extra classes for the window
 *
 * @example
 * // Recap video with controls
 * <XPVideoPlayer src={`${process.env.PUBLIC_URL}/videos/rewind_recap.mp4`} />
 *
 * @example
 * // Animated flyer looping silently
 * <XPVideoPlayer
 *   src={`${process.env.PUBLIC_URL}/videos/rewind_flyer.mp4`}
 *   title="rewind_flyer.mp4"
 *   mode="ambient"
 * />
 */
const XPVideoPlayer = ({
  src,
  title,
  poster,
  mode = 'player',
  label,
  className = '',
}) => {
  const windowTitle = title ?? src.split('/').pop().split('#')[0];
  const ambient = mode === 'ambient';

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Mirror the media element's own state rather than tracking it separately —
  // playback can also be started or stopped outside these controls.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || ambient) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => setCurrentTime(video.currentTime);
    const onMeta = () => setDuration(video.duration);
    const onVolume = () => setIsMuted(video.muted);
    const onEnded = () => setIsPlaying(false);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('durationchange', onMeta);
    video.addEventListener('volumechange', onVolume);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('durationchange', onMeta);
      video.removeEventListener('volumechange', onVolume);
      video.removeEventListener('ended', onEnded);
    };
  }, [ambient]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }, []);

  const stop = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
    setCurrentTime(0);
  }, []);

  const seek = useCallback((e) => {
    const video = videoRef.current;
    const next = Number(e.target.value);
    setCurrentTime(next);
    if (video) video.currentTime = next;
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (video) video.muted = !video.muted;
  }, []);

  const goFullscreen = useCallback(() => {
    const video = videoRef.current;
    if (video?.requestFullscreen) video.requestFullscreen().catch(() => {});
    else if (video?.webkitEnterFullscreen) video.webkitEnterFullscreen(); // iOS Safari
  }, []);

  // Guard against a zero/NaN duration so the range stays operable pre-metadata.
  const seekMax = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const progress = seekMax ? (currentTime / seekMax) * 100 : 0;

  return (
    <CyberLunaWindow
      isStatic
      title={windowTitle}
      className={className}
      bodyClassName={styles.playerBody}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        aria-label={label}
        className={`${styles.video} ${ambient ? '' : styles.clickable}`}
        playsInline
        onClick={ambient ? undefined : togglePlay}
        {...(ambient
          ? { autoPlay: true, muted: true, loop: true }
          : { preload: 'metadata' })}
      />

      {!ambient && (
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.btn}
            onClick={togglePlay}
            title={isPlaying ? 'Pause' : 'Play'}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>

          <button
            type="button"
            className={styles.btn}
            onClick={stop}
            title="Stop"
            aria-label="Stop"
          >
            <StopIcon />
          </button>

          <input
            type="range"
            className={styles.seek}
            style={{ '--progress': `${progress}%` }}
            min="0"
            max={seekMax || 0}
            step="0.1"
            value={Math.min(currentTime, seekMax || 0)}
            onChange={seek}
            aria-label="Seek"
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(seekMax)}`}
          />

          <span className={styles.time}>
            {formatTime(currentTime)} / {formatTime(seekMax)}
          </span>

          <button
            type="button"
            className={styles.btn}
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MutedIcon /> : <SoundIcon />}
          </button>

          <button
            type="button"
            className={styles.btn}
            onClick={goFullscreen}
            title="Full screen"
            aria-label="Full screen"
          >
            <FullscreenIcon />
          </button>
        </div>
      )}
    </CyberLunaWindow>
  );
};

export default XPVideoPlayer;
